import { HttpErrorResponse } from '@angular/common/http';
import type {
  ChatSuggestion,
  ChatSuggestionAdmin,
  ChatSuggestionDynamoLike,
  ChatSuggestionsAdminDetailHttpInput,
  ChatSuggestionsAdminListHttpInput,
  ChatSuggestionsAppResult,
  ChatSuggestionsPublicListPayload,
  ChatSuggestionsSuccessCheckable,
} from '../models/chat-suggestions-api.model';

/** Parses `result.status` from chat-suggestions wire JSON (number or numeric string). */
export function parseChatSuggestionsHttpResultStatus(
  res: ChatSuggestionsSuccessCheckable | null | undefined
): number | null {
  if (!res || typeof res !== 'object') {
    return null;
  }
  const result = res.result;
  if (!result || typeof result !== 'object') {
    return null;
  }
  const s = (result as ChatSuggestionsAppResult).status;
  if (typeof s === 'number' && !Number.isNaN(s)) {
    return s;
  }
  if (typeof s === 'string') {
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Ensures `result.status` parses to HTTP 200 for chat-suggestions wire JSON.
 *
 * @throws HttpErrorResponse when the body is empty or status is not 200.
 */
export function assertSuccessEnvelope(
  res: ChatSuggestionsSuccessCheckable | null | undefined
): asserts res is ChatSuggestionsSuccessCheckable {
  if (!res || typeof res !== 'object') {
    throw new HttpErrorResponse({ status: 0, statusText: 'Empty response' });
  }
  const status = parseChatSuggestionsHttpResultStatus(res);
  if (status !== 200) {
    throw new HttpErrorResponse({
      error: res,
      status: status ?? 500,
      statusText: 'Unexpected result.status',
    });
  }
}

/**
 * Coerces DynamoDB / JSON `active` shapes to boolean for admin UI.
 *
 * @returns `null` only when the value is present but not interpretable as a flag.
 */
export function coerceSuggestionActive(
  value: boolean | number | string | undefined | null
): boolean | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') {
      return true;
    }
    if (s === 'false' || s === '0' || s === 'no' || s === '') {
      return false;
    }
  }
  return null;
}

/**
 * Maps a raw API/DynamoDB row into {@link ChatSuggestionAdmin}. Accepts `key` or `suggestionKey`,
 * and common `active` encodings (boolean, 0/1, string flags).
 */
export function normalizeRawAdminRow(
  value: ChatSuggestionAdmin | ChatSuggestionDynamoLike
): ChatSuggestionAdmin | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const o = value as ChatSuggestionDynamoLike;
  const key =
    typeof o.key === 'string'
      ? o.key
      : typeof o.suggestionKey === 'string'
        ? o.suggestionKey
        : null;
  const agentId = typeof o.agentId === 'string' && o.agentId.trim() ? o.agentId.trim() : undefined;
  if (!key || typeof o.cta !== 'string' || typeof o.macro !== 'string') {
    return null;
  }
  let active = coerceSuggestionActive(o.active);
  if (active === null) {
    active = true;
  }
  const row: ChatSuggestionAdmin = {
    key,
    cta: o.cta,
    macro: o.macro,
    active,
  };
  if (agentId !== undefined) {
    row.agentId = agentId;
  }
  const version = o.version;
  if (typeof version === 'string' || typeof version === 'number') {
    row.version = version;
  }
  const createdAt = o.createdAt;
  if (typeof createdAt === 'string') {
    row.createdAt = createdAt;
  }
  const modifiedAt = o.modifiedAt;
  if (typeof modifiedAt === 'string') {
    row.modifiedAt = modifiedAt;
  }
  return row;
}

/**
 * Extracts `payload.suggestions` (or top-level `suggestions`) after a successful envelope check.
 *
 * @param res Parsed JSON body from the admin list API.
 * @returns Normalized admin rows.
 */
/** Wire body for public `GET …/suggestions` beyond {@link ChatSuggestionsSuccessCheckable}. */
export type ChatSuggestionsPublicListHttpInput = ChatSuggestionsSuccessCheckable & {
  payload?: Partial<ChatSuggestionsPublicListPayload>;
  suggestions?: unknown[];
};

/**
 * Maps one public suggestion row; fills missing `agentId` from the request query param.
 *
 * @param value Raw JSON item from `payload.suggestions`.
 * @param fallbackAgentId `agentId` query param when the row omits `agentId`.
 */
export function normalizePublicSuggestionRow(
  value: unknown,
  fallbackAgentId: string
): ChatSuggestion | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const o = value as Record<string, unknown>;
  const key = typeof o['key'] === 'string' ? o['key'] : null;
  const cta = typeof o['cta'] === 'string' ? o['cta'] : null;
  const macro = typeof o['macro'] === 'string' ? o['macro'] : null;
  if (!key || !cta || !macro) {
    return null;
  }
  const agentId =
    typeof o['agentId'] === 'string' && o['agentId'].trim()
      ? o['agentId'].trim()
      : fallbackAgentId;
  return { key, cta, macro, agentId };
}

/**
 * Parses a successful public suggestions list response.
 *
 * @param res Parsed JSON from `GET …/chat/suggestions`.
 * @param fallbackAgentId Query `agentId` used for rows missing that field.
 * @returns Parsed items and whether the result may be session-memoized.
 */
export function extractPublicSuggestionList(
  res: ChatSuggestionsPublicListHttpInput | null | undefined,
  fallbackAgentId: string
): { items: ChatSuggestion[]; memoizeSession: boolean } {
  if (!res || typeof res !== 'object' || parseChatSuggestionsHttpResultStatus(res) !== 200) {
    return { items: [], memoizeSession: false };
  }

  let raw: unknown[] | undefined;
  const payload = res.payload;
  if (payload?.suggestions && Array.isArray(payload.suggestions)) {
    raw = payload.suggestions;
  }
  if (!raw && Array.isArray(res.suggestions)) {
    raw = res.suggestions;
  }
  if (!raw) {
    return { items: [], memoizeSession: false };
  }
  if (raw.length === 0) {
    return { items: [], memoizeSession: true };
  }

  const items = raw
    .map((item) => normalizePublicSuggestionRow(item, fallbackAgentId))
    .filter((item): item is ChatSuggestion => item !== null);

  if (items.length === 0) {
    return { items: [], memoizeSession: false };
  }

  return { items, memoizeSession: true };
}

export function extractAdminSuggestionList(res: ChatSuggestionsAdminListHttpInput): ChatSuggestionAdmin[] {
  assertSuccessEnvelope(res);
  let raw: ReadonlyArray<ChatSuggestionAdmin | ChatSuggestionDynamoLike> | undefined;
  const payload = res.payload;
  if (payload?.suggestions && Array.isArray(payload.suggestions)) {
    raw = payload.suggestions;
  }
  if (!raw && Array.isArray(res.suggestions)) {
    raw = res.suggestions;
  }
  if (!raw) {
    return [];
  }
  return raw.map(normalizeRawAdminRow).filter((row): row is ChatSuggestionAdmin => row !== null);
}

/**
 * Extracts a single admin-shaped suggestion from `payload.suggestion` or first valid in
 * `payload.suggestions`.
 *
 * @param res Parsed JSON body from the admin detail API (or list-shaped fallback).
 * @returns One row or `null` if not found.
 */
export function extractAdminSuggestionOne(res: ChatSuggestionsAdminDetailHttpInput): ChatSuggestionAdmin | null {
  assertSuccessEnvelope(res);
  const payload = res.payload;
  if (!payload) {
    return null;
  }
  const single = payload.suggestion;
  const fromSingle = single ? normalizeRawAdminRow(single) : null;
  if (fromSingle) {
    return fromSingle;
  }
  const list = payload.suggestions;
  if (Array.isArray(list)) {
    for (const item of list) {
      const row = normalizeRawAdminRow(item);
      if (row) {
        return row;
      }
    }
  }
  return null;
}
